import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';

export type ModalStackEntryKind = 'modal' | 'dialog';

export const MODAL_Z_INDEX = 1000;
export const DIALOG_Z_INDEX = 1002;
const MODAL_BACKDROP_Z_INDEX = MODAL_Z_INDEX - 1;
const DIALOG_BACKDROP_Z_INDEX = DIALOG_Z_INDEX - 1;

export const fullViewportFixed = `
    position: fixed;
    inset: 0;
`;

interface StackEntry {
    id: string;
    kind: ModalStackEntryKind;
}

interface ModalStackValue {
    registerEntry: (id: string, kind: ModalStackEntryKind) => void;
    unregisterEntry: (id: string) => void;
    isTopEntry: (id: string) => boolean;
    isModalOccluded: (id: string) => boolean;
    hasOpenEntry: boolean;
}

const ModalStackContext = createContext<ModalStackValue | null>(null);

const Backdrop = styled.div`
    ${fullViewportFixed}
    background-color: rgba(0, 0, 0, 0.5);
    pointer-events: none;
`;

export function ModalStackProvider({ children }: { children: React.ReactNode }) {
    const [stack, setStack] = useState<StackEntry[]>([]);

    const registerEntry = useCallback((id: string, kind: ModalStackEntryKind) => {
        setStack((previous) => [...previous, { id, kind }]);
    }, []);

    const unregisterEntry = useCallback((id: string) => {
        setStack((previous) => previous.filter((entry) => entry.id !== id));
    }, []);

    const isTopEntry = useCallback((id: string) => stack[stack.length - 1]?.id === id, [stack]);

    const isModalOccluded = useCallback(
        (id: string) => {
            const index = stack.findIndex((entry) => entry.id === id);
            if (index === -1) return false;
            for (let i = index + 1; i < stack.length; i++) {
                if (stack[i].kind === 'modal') return true;
            }
            return false;
        },
        [stack],
    );

    const hasOpenEntry = stack.length > 0;
    const hasOpenDialog = stack.some((entry) => entry.kind === 'dialog');
    const backdropZIndex = hasOpenDialog ? DIALOG_BACKDROP_Z_INDEX : MODAL_BACKDROP_Z_INDEX;

    useEffect(() => {
        if (!hasOpenEntry) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [hasOpenEntry]);

    return (
        <ModalStackContext.Provider
            value={{ registerEntry, unregisterEntry, isTopEntry, isModalOccluded, hasOpenEntry }}
        >
            {children}
            {hasOpenEntry && <Backdrop style={{ zIndex: backdropZIndex }} data-testid="modal-stack-backdrop" />}
        </ModalStackContext.Provider>
    );
}

export function useModalStack(): ModalStackValue {
    const value = useContext(ModalStackContext);
    if (!value) {
        return {
            registerEntry: () => {},
            unregisterEntry: () => {},
            isTopEntry: () => true,
            isModalOccluded: () => false,
            hasOpenEntry: false,
        };
    }
    return value;
}

interface UseModalStackEntryResult {
    id: string;
    isTop: boolean;
    isOccluded: boolean;
}

export function useModalStackEntry(active: boolean, kind: ModalStackEntryKind): UseModalStackEntryResult {
    const id = useId();
    const { registerEntry, unregisterEntry, isTopEntry, isModalOccluded } = useModalStack();

    // useLayoutEffect (not useEffect) so register/unregister commits before paint —
    // otherwise a modal opening on top of another renders for one frame with
    // isOccluded=false because its id isn't in the stack yet.
    useLayoutEffect(() => {
        if (!active) return;
        registerEntry(id, kind);
        return () => unregisterEntry(id);
    }, [active, id, kind, registerEntry, unregisterEntry]);

    return { id, isTop: isTopEntry(id), isOccluded: isModalOccluded(id) };
}
